import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import qs from 'qs';

import Table from '../Table';

const axiosClient = axios.create({
	withCredentials: true
});

const propTypes = {

	/**
	 * The remote URL to use to fetch data.
	 */
	url: PropTypes.string,

	/**
	 * The initial params to use for the data fetching.
	 */
	params: PropTypes.shape({

		/**
		 * The initial page index.
		 */
		page: PropTypes.number,

		/**
		 * The initial page size.
		 */
		page_size: PropTypes.number,

		/**
		 * The property name to sort by initially.
		 */
		sort_by: PropTypes.string,

		/**
		 * The sort order to sort by initially.
		 */
		sort_order: PropTypes.oneOf(['ASC', 'DESC']),

		/**
		 * The default search state for more complex queries.
		 * (e.g. { { user: ['first_name', 'last_name', 'email'] } })
		 */
		search_by: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),

		/**
		 * The default plain text search query, if any.
		 */
		search: PropTypes.string,

	}),

	/**
	 * Flag indicating whether pagination should be enabled
	 * on the table.
	 */
	pagination: PropTypes.bool,

	/**
	 * Callback function to handle clicks to a row of data.
	 */
	onRowClick: PropTypes.func,

	/**
	 * Callback function to handle updates to the total count of items.
	 * Make sure this is memoized with useCallback!
	 */
	onTotalCountChange: PropTypes.func,

	/**
	 * Optional callback function to fetch table data remotely rather
	 * than with the URL endpoint.
	 * Make sure this is memoized with useCallback!
	 */
	onFetchData: PropTypes.func,

};

const defaultProps = {
	params: {},
	pagination: false,
};

/**
 * Component to fetch and display data from the backend.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The DataTable component.
 */
const DataTable = (props) => {

	const { url, params, onTotalCountChange, onFetchData, ...restProps } = props;

	const [data, setData] = useState({
		results: [],
		count: 0,
		page_count: 0,
		initial_load: true,
		error: false,
	});

	// Keep track of when we should show the loading spinner
	const [showLoader, setShowLoader] = useState(false);

	// Keep track of when we are actively loading data
	const [isLoading, setIsLoading] = useState(false);

	const fetchData = useCallback(({ pageSize, pageIndex, sortBy, globalFilter }) => {

		if (url || onFetchData) {

			// Build the query parameters for the API request
			const query = {};

			if (sortBy && sortBy.length > 0) {
				// NOTE: for now we will only support sorting by one field at a time
				query.sort_by = sortBy[0].id;
				query.sort_order = sortBy[0].desc ? 'DESC' : 'ASC';
			}

			if (globalFilter && globalFilter.length > 0) {

				// Build complex search query if search_by prop provided
				const search = {};

				for (const model in params.search_by) {

					if (params.search_by.hasOwnProperty(model)) {
						search[model] = {};

						const fields = params.search_by[model] || [];

						for (let i = 0, len = fields.length; i < len; i++) {
							search[model][fields[i]] = globalFilter;
						}

					}

				}

				query.search = params.search_by ? search : globalFilter;

			}

			if (props.pagination) {
				query.page = pageIndex + 1;
				query.page_size = pageSize;
			}

			const args = {
				params: query,
				paramsSerializer: (requestParams) => {
					return qs.stringify(requestParams, {
						arrayformat: 'brackets',
						encode: false,
						skipNulls: true,
					});
				}
			};

			let promise;

			// Check if custom callback should be used to fetch data
			if (onFetchData) {

				promise = onFetchData(args);

			// Otherwise use provided API endpoint
			} else if (url) {

				promise = axiosClient.get(url, args);

			}

			setIsLoading(true);

			promise.then((response) => {

				let count = response.headers && parseInt(response.headers['x-total-count'], 10);

				if (isNaN(count)) {
					count = 0;
				}

				if (onTotalCountChange) {
					onTotalCountChange(count);
				}

				setIsLoading(false);

				setData((prev) => ({
					...prev,
					results: response.data,
					count,
					page_count: Math.ceil(count / pageSize),
					error: false,
					initial_load: false,
				}));

			}).catch((error) => {

				console.error(error);

				setIsLoading(false);

				setData((prev) => ({
					...prev,
					results: [],
					count: 0,
					page_count: 0,
					error: true,
					initial_load: false,
				}));

			});

		}

	}, [
		url,
		params.search_by,
		props.pagination,
		onTotalCountChange,
		onFetchData
	]);

	/**
	 * Briefly delay updating the loading state to prevent render flashes
	 * for quick API requests. We'll only show the loading spinner if the request
	 * takes more than 100ms, otherwise we'll clear the timeout and cancel the update.
	 */
	useEffect(() => {

		const timeoutID = setTimeout(() => {
			setShowLoader(isLoading);
		}, 100);

		return () => {
			clearTimeout(timeoutID);
		};

	}, [isLoading]);

	return (
		<Table
			data={data.results}
			initialParams={{
				page: params.page,
				page_size: params.page_size,
				sort_by: params.sort_by,
				sort_order: params.sort_order,
				search: params.search,
			}}
			fetchData={fetchData}
			loadingData={showLoader}
			initializing={data.initial_load}
			remotePageCount={data.page_count}
			hasError={data.error}
			{...restProps} />
	);

};

DataTable.propTypes = propTypes;
DataTable.defaultProps = defaultProps;

export default DataTable;
